import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type { AccountWriteResponse } from '@/api/handlers/resources/accounts/responses';
import type { ApiResponse } from '@/api/handlers/responses';
import { AccountQueryKeys } from './accountQueryKeys';

export type SetAccountArchivedVariables = {
    id: string;
    archived: boolean;
};

/**
 * Archive (`archived: true`) or reactivate (`archived: false`) an account.
 *
 * Archiving is server-gated on a zero balance (`ARCHIVE_NONZERO_BALANCE`) and no
 * active children (`ARCHIVE_HAS_ACTIVE_CHILDREN`); reactivation is
 * unconditional. Archiving is **not** deletion — history stays, the account just
 * leaves the default chart and reappears under "Show archived". It is the only
 * removal path this client offers; `DELETE /accounts/{id}` exists on the API and
 * is deliberately unreachable from here.
 *
 * Both endpoints answer `200` with the updated account and a **fresh etag** (they
 * used to be `204`), so this is an ordinary write. Invalidating the accounts key
 * drops the cached detail holding the now-spent token, which is what stops a
 * subsequent edit from 409-ing on a stale etag.
 *
 * Never rejects — branch on `result.success` and read `result.error.code`.
 */
export const useSetAccountArchivedMutation = () => {
    const { apiClient } = useApi();
    const queryClient = useQueryClient();

    return useMutation<
        ApiResponse<AccountWriteResponse>,
        Error,
        SetAccountArchivedVariables
    >({
        mutationFn: ({ id, archived }) =>
            apiClient.accounts.setAccountArchived(id, archived),
        onSuccess: (result) => {
            if (result.success) {
                queryClient.invalidateQueries({
                    queryKey: AccountQueryKeys.all,
                });
            }
        },
    });
};
